import { useMemo, useRef, useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";
import type { TermsFacet as TermsFacetData } from "@/features/search/api/types";
import { useSearchContext } from "@/features/search/context/SearchContext";
import { useFacetValueLabel } from "@/features/search/hooks/useFacetLabel";
import { formatResultCount } from "@/features/search/utils/count-placeholder.utils";
import { toggleTermValue } from "@/features/search/utils/search-params.utils";
import { CheckBox } from "@/ui/cva/check-box/CheckBox";

const COLLAPSED_COUNT = 8;

type TermsFacetProps = {
  facet: TermsFacetData;
};

/**
 * Multi-select term facet. Counts and selected state come straight from the
 * API (disjunctive), so this only renders and toggles — no client-side counting.
 *
 * A value the landing page itself applies is shown checked but not interactive: the URL
 * can't clear it — the service marks it `locked` — so a working checkbox would be a lie.
 * Widening is the breadcrumb's job.
 */
export const TermsFacet = ({ facet }: TermsFacetProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { navigation } = useSearchContext();
  const facetValueLabel = useFacetValueLabel();
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  // Selected first, then by count — keeps active + relevant options in view.
  const values = useMemo(
    () =>
      [...facet.values].sort(
        (left, right) =>
          Number(right.selected) - Number(left.selected) ||
          right.count - left.count
      ),
    [facet.values]
  );

  const visible = expanded ? values : values.slice(0, COLLAPSED_COUNT);

  return (
    <div className="flex flex-col space-y-2">
      {/*
       * Expanded lists scroll past 16 rows rather than pushing every other facet off
       * screen — some facets have 100+ values. 27.5rem = 16 rows (text-sm line-height
       * 1.25rem) plus the 15 space-y-2 (0.5rem) gaps between them.
       *
       * Desktop only: below `md` the facets live in the mobile drawer, which scrolls
       * itself. A scroll box nested in it swallows every swipe, so you can't scroll
       * past the list to the facets below.
       *
       * overscroll-contain: at the end of the list, further scrolling would otherwise
       * hand off to the page and drag the whole sidebar and grid along with it.
       *
       * Native scrolling, not the Radix ScrollArea. Radix draws its own indicator from
       * JS and adds a non-passive document wheel listener, so on a busy page the wheel
       * stalls and the indicator drifts away from the real scroll position.
       *
       * pr-3 keeps the count clear of the scrollbar.
       */}
      <ul
        ref={listRef}
        className="flex flex-col space-y-2 pr-3 [scrollbar-color:theme(colors.grey.300)_transparent] [scrollbar-width:thin] md:max-h-[27.5rem] md:overflow-y-auto md:overscroll-contain"
      >
        {visible.map(value => {
          const isLocked = value.locked;

          return (
            <li key={value.value}>
              <label
                className={cn(
                  "flex items-center space-x-2 font-body text-sm",
                  isLocked ? "cursor-default" : "cursor-pointer",
                  value.count === 0 && !value.selected && "opacity-40"
                )}
              >
                <CheckBox
                  checked={value.selected}
                  disabled={isLocked}
                  aria-disabled={isLocked || undefined}
                  onChange={() => {
                    if (isLocked) {
                      return;
                    }

                    navigation.toggleTerm(facet.key, value.value);

                    /* Selected values sort to the top, so follow the ticked one there — in a
                     * long expanded list it would otherwise vanish above the fold. Scrolls
                     * the list on desktop and the drawer on mobile. */
                    listRef.current?.firstElementChild?.scrollIntoView({
                      block: "nearest",
                    });

                    /* Reports the selection *after* the toggle, which is what the gateway PLP
                     * sent — the resulting filter state, not the value that changed. */
                    analyticsService.trackFilterClickList(
                      facet.key,
                      toggleTermValue(
                        facet.values
                          .filter(option => option.selected)
                          .map(option => option.value),
                        value.value
                      )
                    );
                  }}
                />
                <span
                  className={cn(
                    "flex-1 truncate",
                    isLocked ? "font-semibold text-grey-500" : "text-black"
                  )}
                >
                  {facetValueLabel(facet.key, value)}
                </span>
                <span className="tabular-nums text-grey-400">
                  {formatResultCount(value.count, locale)}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {/* Outside the scroll area so it stays reachable without scrolling to the end. */}
      {values.length > COLLAPSED_COUNT && (
        <button
          type="button"
          onClick={() => setExpanded(isExpanded => !isExpanded)}
          className="self-start font-body text-sm font-semibold text-brand-500 hover:underline"
        >
          {expanded
            ? t("common:common-show-less")
            : t("common:common-show-more")}
        </button>
      )}
    </div>
  );
};
